import { useApolloClient } from "@apollo/client";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  assert,
  assertNever,
  getDataOrNull,
  getNameWithCopy,
  throwIfUndefined,
} from "@msys/common";
import { Modal, ModalOpenButton, SplitButton } from "@msys/ui";
import {
  BookmarkAdd as BookmarkAddIcon,
  BookmarkRemove as BookmarkRemoveIcon,
  ControlPointDuplicate as ControlPointDuplicateIcon,
  Delete as DeleteIcon,
  GroupAdd as GroupAddIcon,
  Publish as PublishIcon,
} from "@mui/icons-material";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Button,
  ButtonProps,
  Checkbox,
  FormControl,
  FormControlLabel,
  Icon,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { DepGraph } from "dependency-graph";
import { Form, Formik } from "formik";
import { keyBy, partition } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { ViewerShapeFragment } from "../../auth/UserDataProvider.generated.js";
import { useUserData } from "../../auth/useUserData.js";
import { download } from "../../browserUtils.js";
import { TagsField } from "../../commons/form-fields/TagsField.js";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import {
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { TemplateQuotes_TemplateFragment } from "../../main-routes/templates/quote/TemplateQuotes.generated.js";
import { CategorySearchModal } from "../categories/CategorySearchModal.js";
import { useJsonExport } from "../jsonImportExport/useJsonExport.js";
import { useOrganisationTags } from "../organisations/useOrganisationTags.js";
import { TemplateTypesSearchModal } from "../templateTypes/TemplateTypesSearchModal.js";
import { useTemplateBulkUpdateProductsMutation } from "./buttons/TemplateBulkUpdateProductsButton.generated.js";
import { usePublishQuoteTemplateMutation } from "./quote/buttons/QuoteTemplatePublishButton.generated.js";
import { useQuoteTemplatePublish } from "./quote/buttons/QuoteTemplatePublishButton.js";
import { QuoteTemplatesShareModal } from "./quote/buttons/QuoteTemplateShareButton.js";
import {
  useSubscribeToQuoteTemplate_HasOverridesLazyQuery,
  useSubscribeToQuoteTemplateMutation,
} from "./quote/buttons/QuoteTemplateSubscribeButton.generated.js";
import { useUnsubscribeFromQuoteTemplateMutation } from "./quote/buttons/QuoteTemplateUnsubscribeButton.generated.js";
import {
  canUpdateShare,
  useQuoteTemplateUpdateShare,
} from "./quote/buttons/QuoteTemplateUpdateShareButton.js";
import {
  useDeleteQuoteTemplateMutation,
  useDuplicateQuoteTemplateMutation,
} from "./quote/TemplateQuotes.generated.js";
import {
  useImplementsTemplateTypeMutation,
  useModifyQuoteTemplateMutation,
} from "./quote/TemplatesQuoteTemplateSettingsModal.generated.js";

type QuickFilterValue =
  | "ALL_AVAILABLE"
  | "MY_ALL"
  | "MY_DRAFT"
  | "MY_PRIVATE"
  | "MY_PROTECTED"
  | "MY_PUBLIC"
  | "MEISTER_STORE_ALL"
  | "MEISTER_STORE_SUBSCRIBED";

type Action =
  | "delete"
  | "duplicate"
  | "publish"
  | "share"
  | "subscribe"
  | "unsubscribe"
  | "export"
  | "sync_products"
  | "assign";

export function useTemplateDocumentsListBulkActions(
  quickFilter: QuickFilterValue,
  selectedItems: TemplateQuotes_TemplateFragment[],
  resetSelectedItems: () => void
) {
  const viewer = useUserData().currentUser!;

  const { t } = useTranslate([
    "Templates",
    "TemplatesSharing",
    "TemplatesPublishing",
  ]);

  const availableActions = getAvailableActionsForQuickFilter(
    quickFilter
  ).filter(action => doesAnyItemSupportAction(viewer, action, selectedItems));

  const buttons: Record<Action, React.ReactNode> = {
    duplicate: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"duplicate"}
        permission="MANAGE_TEMPLATE"
      >
        <DuplicateItemsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    publish: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"publish"}
        permission="MANAGE_TEMPLATE"
      >
        <PublishItemsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    share: (
      <RestrictedByCapabilityWithDebug
        key={"share"}
        capability="TEMPLATE_SHARING"
      >
        <RestrictedByOrganisationPermissionWithDebug permission="SHARE_TEMPLATE">
          <ShareItemsButton
            items={selectedItems}
            handleComplete={resetSelectedItems}
          />
        </RestrictedByOrganisationPermissionWithDebug>
      </RestrictedByCapabilityWithDebug>
    ),
    subscribe: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"subscribe"}
        permission="SHARE_TEMPLATE"
      >
        <SubscribeItemsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    unsubscribe: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"unsubscribe"}
        permission="SHARE_TEMPLATE"
      >
        <UnsubscribeItemsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    export: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"export"}
        permission="MANAGE_TEMPLATE"
      >
        <ExportButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    sync_products: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"sync_products"}
        permission="MANAGE_TEMPLATE"
      >
        <SyncItemsProductsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    delete: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"delete"}
        permission="MANAGE_TEMPLATE"
      >
        <DeleteItemsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
    assign: (
      <RestrictedByOrganisationPermissionWithDebug
        key={"assign"}
        permission="MANAGE_TEMPLATE_SETTINGS"
      >
        <AssignItemsButton
          items={selectedItems}
          handleComplete={resetSelectedItems}
        />
      </RestrictedByOrganisationPermissionWithDebug>
    ),
  };

  const actionButtons =
    selectedItems.length > 0
      ? availableActions.length === 0
        ? t("No actions available", {
            ns: "Templates",
          })
        : availableActions.map(action => buttons[action])
      : [];

  return { actionButtons };
}

function getAvailableActionsForQuickFilter(
  quickFilter: QuickFilterValue
): Action[] {
  const quickFilterAvailableActions: Record<QuickFilterValue, Action[]> = {
    ALL_AVAILABLE: ["export"],
    MY_ALL: [
      "duplicate",
      "publish",
      "share",
      "export",
      "sync_products",
      "delete",
      "assign",
    ],
    MY_DRAFT: [
      "duplicate",
      "publish",
      "export",
      "sync_products",
      "delete",
      "assign",
    ],
    MY_PRIVATE: [
      "duplicate",
      "share",
      "export",
      "sync_products",
      "delete",
      "assign",
    ],
    MY_PROTECTED: [
      "duplicate",
      "share",
      "export",
      "sync_products",
      "delete",
      "assign",
    ],
    MY_PUBLIC: [
      "duplicate",
      "share",
      "export",
      "sync_products",
      "delete",
      "assign",
    ],
    MEISTER_STORE_ALL: ["subscribe", "unsubscribe"],
    MEISTER_STORE_SUBSCRIBED: ["unsubscribe"],
  };

  return quickFilterAvailableActions[quickFilter];
}

function doesAnyItemSupportAction(
  viewer: ViewerShapeFragment,
  action: Action,
  items: TemplateQuotes_TemplateFragment[]
) {
  switch (action) {
    case "duplicate":
      return items.some(
        item => item.owningSystemOrganisationId === viewer.organisation.id
      );
    case "publish":
      return items.some(item => !item.hasAnyPublishedVersion);
    case "share":
      return items.some(
        item =>
          item.templateSharing?.sharedPublicly === false &&
          item.hasAnyPublishedVersion
      );
    case "subscribe":
      return items.some(item => !item.templateSharing?.subscribedToTemplate);
    case "unsubscribe":
      return items.some(item => !!item.templateSharing?.subscribedToTemplate);
    case "export":
      return items.some(
        item => item.owningSystemOrganisationId === viewer.organisation.id
      );
    case "sync_products":
      return items.some(
        item => item.owningSystemOrganisationId === viewer.organisation.id
      );
    case "delete":
      return items.some(
        item => item.owningSystemOrganisationId === viewer.organisation.id
      );
    case "assign":
      return true;
    default:
      return assertNever(action);
  }
}

function AssignItemsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete: () => void;
}) {
  const { t } = useTranslate("Templates");
  const { enqueueSnackbar } = useSnackbar();

  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const client = useApolloClient();
  const [implementsTemplateType] = useImplementsTemplateTypeMutation({
    client,
  });
  const [modifyQuoteTemplate] = useModifyQuoteTemplateMutation({
    client,
    refetchQueries: [
      namedOperations.Query.OrganisationTags,
      // ...(refetchQueries ?? []),
    ],
    awaitRefetchQueries: true,
  });

  return (
    <SplitButton
      buttons={[
        <ModalOpenButton
          Modal={TemplateTypesSearchModal}
          modalProps={{
            handleSelectTemplateType: async (templateType, handleClose) => {
              await Promise.allSettled(
                items.map(async item => {
                  await implementsTemplateType({
                    variables: {
                      input: {
                        templateId: item.id,
                        templateTypeId: templateType.id,
                        templateTypePropertiesStrategy:
                          "keepPropertiesWithMerging",
                      },
                    },
                  });
                })
              ).then(results => {
                const [success, fail] = partition(
                  results,
                  result => result.status === "fulfilled"
                );

                enqueueSnackbar(
                  t(
                    "{count, plural, =0 {No templates} one {One template} other {# templates}} changed",
                    {
                      ns: "Templates",
                      count: success.length,
                    }
                  )
                );
                if (fail.length > 0) {
                  enqueueSnackbar(
                    t(
                      "{count, plural, one {One template} other {# templates}} failed to be changed",
                      {
                        ns: "Templates",
                        count: fail.length,
                      }
                    ),
                    { variant: "error" }
                  );
                }
              });

              handleClose();
              handleComplete();
            },
          }}
        >
          <Button>
            {t("Assign template type", {
              ns: "Templates",
            })}
          </Button>
        </ModalOpenButton>,
        <ModalOpenButton
          Modal={CategorySearchModal}
          modalProps={{
            handleSelectCategory: async (category, handleClose) => {
              await Promise.allSettled(
                items.map(async item => {
                  await modifyQuoteTemplate({
                    variables: {
                      input: {
                        quoteTemplateId: item.id,
                        values: {
                          category: category.id,
                        },
                      },
                    },
                  });
                })
              ).then(results => {
                const [success, fail] = partition(
                  results,
                  result => result.status === "fulfilled"
                );

                enqueueSnackbar(
                  t(
                    "{count, plural, =0 {No templates} one {One template} other {# templates}} changed",
                    {
                      ns: "Templates",
                      count: success.length,
                    }
                  )
                );
                if (fail.length > 0) {
                  enqueueSnackbar(
                    t(
                      "{count, plural, one {One template} other {# templates}} failed to be changed",
                      {
                        ns: "Templates",
                        count: fail.length,
                      }
                    ),
                    { variant: "error" }
                  );
                }
              });

              handleClose();
              handleComplete();
            },
          }}
        >
          <Button>
            {t("Assign template category", {
              ns: "Templates",
            })}
          </Button>
        </ModalOpenButton>,
        <ModalOpenButton
          Modal={SelectTagsModal}
          modalProps={{
            handleComplete: async (tags, handleClose) => {
              await Promise.allSettled(
                items.map(async item => {
                  await modifyQuoteTemplate({
                    variables: {
                      input: {
                        quoteTemplateId: item.id,
                        values: {
                          tags: Array.from(
                            new Set(item.customTags.concat(tags))
                          ),
                        },
                      },
                    },
                  });
                })
              ).then(results => {
                const [success, fail] = partition(
                  results,
                  result => result.status === "fulfilled"
                );

                enqueueSnackbar(
                  t(
                    "{count, plural, =0 {No templates} one {One template} other {# templates}} changed",
                    {
                      ns: "Templates",
                      count: success.length,
                    }
                  )
                );
                if (fail.length > 0) {
                  enqueueSnackbar(
                    t(
                      "{count, plural, one {One template} other {# templates}} failed to be changed",
                      {
                        ns: "Templates",
                        count: fail.length,
                      }
                    ),
                    { variant: "error" }
                  );
                }
              });

              handleClose();
              handleComplete();
            },
          }}
        >
          <Button>{t("Add tags", { ns: "Templates" })}</Button>
        </ModalOpenButton>,
      ]}
      selectedIndex={selectedIndex}
      setSelectedIndex={setSelectedIndex}
    />
  );
}

function SelectTagsModal({
  handleClose,
  handleComplete,
}: {
  handleClose: () => void;
  handleComplete: (tags: Array<string>, handleClose: () => void) => void;
}) {
  const { t } = useTranslate(["Templates", "Global"]);

  const { customTags } = useOrganisationTags("TEMPLATE");

  function handleSubmit(values: { tags: Array<{ value: string }> }) {
    handleComplete(
      values.tags.map(tag => tag.value),
      handleClose
    );
  }

  const formId = React.useId();

  return (
    <Formik initialValues={{ tags: [] }} onSubmit={handleSubmit}>
      <Modal
        handleClose={handleClose}
        title={t("Add tags", { ns: "Templates" })}
        actionButtons={[
          {
            label: t("Cancel", { ns: "Global" }),
            handleClick: () => handleClose(),
            buttonProps: {
              variant: "text",
            },
          },
          {
            label: t("Save", { ns: "Global" }),
            buttonProps: {
              type: "submit",
              form: formId,
            },
          },
        ]}
      >
        <Form id={formId}>
          <TagsField
            name="tags"
            options={customTags}
            inputLabel={t("Tags", { ns: "Templates" })}
          />
        </Form>
      </Modal>
    </Formik>
  );
}

function ExportButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { t } = useTranslate("Templates");
  const [exportTemplates, { isLoading }] = useJsonExport();
  const viewer = useUserData().currentUser!;

  return (
    <Button
      variant="contained"
      startIcon={
        <Icon>
          <FontAwesomeIcon icon={faFileExport} />
        </Icon>
      }
      onClick={async () => {
        const { downloadUrl, filename } = await exportTemplates(
          items
            .filter(
              item => item.owningSystemOrganisationId === viewer.organisation.id
            )
            .map(item => ({
              docId: item.id,
              versionNumber: item.resolvedAsReadModelVersionNumber ?? null,
            }))
        );
        download(downloadUrl, filename);
        if (handleComplete) handleComplete();
      }}
      loading={isLoading}
    >
      {t("Export templates")}
    </Button>
  );
}

function DuplicateItemsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["Quote", "Global"]);
  const viewer = useUserData().currentUser!;

  const client = useApolloClient();
  const [duplicateQuoteTemplateMutation, { loading }] =
    useDuplicateQuoteTemplateMutation({
      client,
    });

  return (
    <BulkActionButton
      text={t("Duplicate", {
        ns: "Quote",
      })}
      action={async item => {
        await duplicateQuoteTemplateMutation({
          variables: {
            input: {
              title: getNameWithCopy(
                item.title,
                t("copy", {
                  ns: "Global",
                })
              ),
              quoteTemplateId: item.id,
            },
          },
        });
      }}
      items={items.filter(
        item => item.owningSystemOrganisationId === viewer.organisation.id
      )}
      startIcon={<ControlPointDuplicateIcon />}
      loading={loading}
      handleComplete={itemCount => {
        if (handleComplete) handleComplete();
        enqueueSnackbar(
          t(
            "{count, plural, =0 {No templates} one {One template} other {# templates}} duplicated",
            {
              ns: "Quote",
              count: itemCount,
            }
          )
        );
      }}
      onFinalized={async () => {
        await client.refetchQueries({
          include: [namedOperations.Query.TemplateQuotes],
        });
      }}
    />
  );
}
function PublishItemsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["TemplatesSharing"]);

  const client = useApolloClient();
  const [publish, { loading }] = usePublishQuoteTemplateMutation({
    client,
  });

  return (
    <BulkActionButton
      text={t("Publish", {
        ns: "TemplatesSharing",
      })}
      action={async item => {
        await publish({ variables: { input: { docId: item.id } } });
      }}
      items={items.filter(item => !item.hasAnyPublishedVersion)}
      startIcon={<PublishIcon />}
      loading={loading}
      handleComplete={itemCount => {
        if (handleComplete) handleComplete();
        enqueueSnackbar(
          t(
            "{count, plural, =0 {No templates} one {One template} other {# templates}} published",
            {
              ns: "TemplatesSharing",
              count: itemCount,
            }
          )
        );
      }}
      onFinalized={async () => {
        await client.refetchQueries({
          include: [namedOperations.Query.TemplateQuotes],
        });
      }}
    />
  );
}

function SubscribeItemsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["TemplatesPublishing", "Global"]);

  const client = useApolloClient();
  const [fetch, query] = useSubscribeToQuoteTemplate_HasOverridesLazyQuery({
    client,
  });
  const [subscribe, { loading }] = useSubscribeToQuoteTemplateMutation({
    client,
  });
  const confirmProcessRef = React.useRef<ConfirmProcessRef>(null);

  return (
    <>
      <BulkActionButton
        text={t("Subscribe", { ns: "TemplatesPublishing" })}
        action={async item => {
          assert(item.templateSharing, "expected templateSharing");
          assert(
            !item.templateSharing.subscribedToTemplate,
            "already subscribed"
          );
          assert(
            item.templateSharing.latestSharedVersionNumber,
            "expected doc.templateSharing.latestSharedVersionNumber"
          );

          let applyOverrides = false;
          const result = await fetch({ variables: { templateId: item.id } });
          assert(
            result?.data?.quoteTemplateHasOverrides,
            "expected quoteTemplateHasOverrides result"
          );
          const latestVersionWithOverrides = getDataOrNull(
            result?.data?.quoteTemplateHasOverrides
          )?.version;

          if (
            latestVersionWithOverrides &&
            latestVersionWithOverrides <
              item.templateSharing.latestSharedVersionNumber
          ) {
            const result = await confirmProcessRef.current?.startConfirmProcess(
              {
                title: t("Applying overrides", { ns: "TemplatesPublishing" }),
                text: t(
                  "You have overrides from a previous version of a template: {title}. Do you want to apply them to new version?",
                  { ns: "TemplatesPublishing", title: item.title }
                ),
                confirmButtonLabel: t("Yes", { ns: "Global" }),
                cancelButtonLabel: t("No", { ns: "Global" }),
              }
            );
            if (result) {
              applyOverrides = true;
            }
          }

          await subscribe({
            variables: {
              input: {
                quoteTemplateId: item.id,
                versionNumber: item.templateSharing.latestSharedVersionNumber,
                applyOverrides,
              },
            },
          });
        }}
        items={items.filter(
          item =>
            item.templateSharing && !item.templateSharing.subscribedToTemplate
        )}
        startIcon={<BookmarkAddIcon />}
        loading={loading || query.loading}
        handleComplete={itemCount => {
          if (handleComplete) handleComplete();
          enqueueSnackbar(
            t(
              "{count, plural, =0 {No templates} one {One template} other {# templates}} subscribed",
              {
                ns: "TemplatesPublishing",
                count: itemCount,
              }
            )
          );
        }}
        onFinalized={async () => {
          await client.refetchQueries({
            include: [namedOperations.Query.TemplateQuotes],
          });
        }}
      />
      <ConfirmProcess ref={confirmProcessRef} />
    </>
  );
}
function UnsubscribeItemsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["TemplatesPublishing"]);

  const client = useApolloClient();
  const [unsubscribe, { loading }] = useUnsubscribeFromQuoteTemplateMutation({
    client,
  });
  return (
    <BulkActionButton
      text={t("Unsubscribe", {
        ns: "TemplatesPublishing",
      })}
      action={async item => {
        await unsubscribe({
          variables: { input: { quoteTemplateId: item.id } },
        });
      }}
      items={items.filter(item => !!item.templateSharing?.subscribedToTemplate)}
      startIcon={<BookmarkRemoveIcon />}
      loading={loading}
      handleComplete={itemCount => {
        if (handleComplete) handleComplete();
        enqueueSnackbar(
          t(
            "{count, plural, =0 {No templates} one {One template} other {# templates}} unsubscribed",
            {
              ns: "TemplatesPublishing",
              count: itemCount,
            }
          )
        );
      }}
      onFinalized={async () => {
        await client.refetchQueries({
          include: [namedOperations.Query.TemplateQuotes],
        });
      }}
    />
  );
}

interface BulkActionProcessProps<Item extends { id: string; title: string }> {
  items: Item[];
  action: (item: Item) => Promise<void>;
  handleComplete?: (itemCount: number) => void;
  sequentially?: boolean;
  onFinalized?(): void | Promise<void>;
}

function useBulkActionProcess<Item extends { id: string; title: string }>({
  items,
  action,
  handleComplete,
  sequentially,
  onFinalized,
}: BulkActionProcessProps<Item>) {
  const { enqueueSnackbar } = useSnackbar();
  return async () => {
    try {
      if (sequentially) {
        for (const item of items) {
          await action(item);
        }
      } else {
        await Promise.all(items.map(action));
      }

      if (handleComplete) handleComplete(items.length);
    } catch (error) {
      if (error instanceof Error)
        enqueueSnackbar(error.message, { variant: "error" });
    } finally {
      onFinalized?.();
    }
  };
}

function BulkActionButton<Item extends { id: string; title: string }>({
  text,
  items,
  action,
  handleComplete,
  sequentially,
  onFinalized,
  ...props
}: {
  text: string;
} & BulkActionProcessProps<Item> &
  Omit<ButtonProps, "action">) {
  const handleProcess = useBulkActionProcess({
    items,
    action,
    handleComplete,
    sequentially,
    onFinalized,
  });
  return (
    <Button variant="contained" onClick={handleProcess} {...props}>
      {text}
    </Button>
  );
}

function ShareItemsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["TemplatesSharing"]);

  return (
    <ModalOpenButton
      Modal={QuoteTemplatesShareModal}
      modalProps={{
        templates: items.filter(
          item =>
            !item.templateSharing?.sharedPublicly && item.hasAnyPublishedVersion
        ),
        handleComplete: count => {
          handleComplete?.();
          enqueueSnackbar(
            t(
              "{count, plural, =0 {No templates} one {One template} other {# templates}} sharing updated",
              {
                ns: "TemplatesSharing",
                count,
              }
            )
          );
        },
      }}
    >
      <Button startIcon={<GroupAddIcon />} variant="contained" color="primary">
        {t("Share", {
          ns: "TemplatesSharing",
        })}
      </Button>
    </ModalOpenButton>
  );
}
function SyncItemsProductsButton({
  items,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void | Promise<void>;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate([
    "Templates",
    "TemplatesSharing",
    "TemplatesPublishing",
  ]);
  const viewer = useUserData().currentUser!;

  const client = useApolloClient();

  const [templateBulkUpdateProducts, { loading }] =
    useTemplateBulkUpdateProductsMutation({
      client,
    });

  const handleUpdateShare = useQuoteTemplateUpdateShare({});
  const handlePublish = useQuoteTemplatePublish();

  const flagsRef = React.useRef<SyncProductsFlags>({
    publishLatestVersions: false,
    publishAlsoToMeisterStore: false,
  });

  const countsRef = React.useRef({
    publishedNumber: 0,
    publishedToMeisterStoreNumber: 0,
  });

  const handleProcess = useBulkActionProcess({
    items: items.filter(
      item => item.owningSystemOrganisationId === viewer.organisation.id
    ),
    action: async item => {
      const result = await templateBulkUpdateProducts({
        variables: {
          input: {
            docId: item.id,
          },
        },
      });

      // 1. Additionally, publish latest version
      if (
        flagsRef.current.publishLatestVersions &&
        result.data?.templateBulkUpdateProducts.doc.hasAnyPublishedVersion &&
        result.data?.templateBulkUpdateProducts.doc
          .containsUnpublishedChanged &&
        // has changes in doc after product prices update
        result.data?.templateBulkUpdateProducts.changedItems.length
      ) {
        const { template, newVersionNumber } = await handlePublish(item.id);
        countsRef.current.publishedNumber++;

        // 2. Additionally, update sharing of latest version to Meister Store
        if (
          template &&
          flagsRef.current.publishAlsoToMeisterStore &&
          canUpdateShare(template, newVersionNumber)
        ) {
          await handleUpdateShare(template, newVersionNumber);
          countsRef.current.publishedToMeisterStoreNumber++;
        }
      }
    },
    handleComplete: async itemCount => {
      await handleComplete?.();
      if (itemCount > 0) {
        enqueueSnackbar(
          t(
            "Synced products for {count, plural, =0 {no template} one {one template} other {# templates}}",
            {
              ns: "Templates",
              count: itemCount,
            }
          )
        );
      }
    },
    onFinalized: async () => {
      if (countsRef.current.publishedNumber > 0) {
        enqueueSnackbar(
          t(
            "New version of {count, plural, =0 {no template} one {one template} other {# templates}} published",
            {
              ns: "TemplatesSharing",
              count: countsRef.current.publishedNumber,
            }
          )
        );
      }
      if (countsRef.current.publishedToMeisterStoreNumber > 0) {
        enqueueSnackbar(
          t(
            "New version of {count, plural, =0 {no template} one {one template} other {# templates}} shared to Meister Store",
            {
              ns: "TemplatesSharing",
              count: countsRef.current.publishedToMeisterStoreNumber,
            }
          )
        );
      }
      await client.refetchQueries({
        include: [namedOperations.Query.TemplateQuotes],
      });
    },
  });

  return (
    <ModalOpenButton
      Modal={SyncProductsConfirmModal}
      modalProps={{
        handleConfirm: async flags => {
          // save flags to ref
          flagsRef.current = flags;
          countsRef.current = {
            publishedNumber: 0,
            publishedToMeisterStoreNumber: 0,
          };
          // start processing
          await handleProcess();
        },
      }}
    >
      <Button variant="contained" startIcon={<SyncIcon />} loading={loading}>
        {t("Sync products", { ns: "Templates" })}
      </Button>
    </ModalOpenButton>
  );
}

type SyncProductsFlags = {
  publishLatestVersions: boolean;
  publishAlsoToMeisterStore: boolean;
};

function SyncProductsConfirmModal({
  handleClose,
  handleConfirm,
}: {
  handleClose: () => void;
  handleConfirm: (flags: SyncProductsFlags) => void | Promise<void>;
}) {
  const { t } = useTranslate(["Templates", "Global", "TemplatesPublishing"]);
  const [flags, setFlags] = React.useState<SyncProductsFlags>({
    publishLatestVersions: false,
    publishAlsoToMeisterStore: false,
  });
  return (
    <ConfirmModal
      handleClose={handleClose}
      handleConfirm={async () => {
        await handleConfirm(flags);
      }}
      title={t("Sync products", { ns: "Templates" })}
      confirmButtonLabel={t("Continue", { ns: "Global" })}
      content={() => (
        <Stack direction="column" spacing={1}>
          <Typography variant="body1">
            {t("Do you want to publish new version of affected templates?", {
              ns: "TemplatesPublishing",
            })}
          </Typography>

          <FormControl variant="standard">
            <FormControlLabel
              control={
                <Checkbox
                  checked={flags.publishLatestVersions}
                  indeterminate={false}
                  onChange={(event, checked) => {
                    setFlags(f => ({ ...f, publishLatestVersions: checked }));
                  }}
                  size="small"
                />
              }
              label={t("Yes, publish new version", {
                ns: "TemplatesPublishing",
              })}
            />
          </FormControl>
          <FormControl variant="standard">
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    flags.publishLatestVersions &&
                    flags.publishAlsoToMeisterStore
                  }
                  disabled={!flags.publishLatestVersions}
                  indeterminate={false}
                  onChange={(event, checked) => {
                    setFlags(f => ({
                      ...f,
                      publishAlsoToMeisterStore: checked,
                    }));
                  }}
                  size="small"
                />
              }
              label={t("Yes, and also publish to Meister Store", {
                ns: "TemplatesPublishing",
              })}
            />
          </FormControl>
        </Stack>
      )}
    />
  );
}

function DeleteItemsButton({
  items: itemsInitial,
  handleComplete,
}: {
  items: TemplateQuotes_TemplateFragment[];
  handleComplete?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["Templates"]);
  const viewer = useUserData().currentUser!;

  const client = useApolloClient();
  const [deleteTemplate, { loading }] = useDeleteQuoteTemplateMutation({
    client,
  });

  const items = React.useMemo(() => {
    const dependencyGraph = new DepGraph<string>({ circular: false });

    const filterdItems = itemsInitial
      .filter(
        item => item.owningSystemOrganisationId === viewer.organisation.id
      )
      .filter(item => !(item.hasAnyPublishedVersion && !!item.templateSharing));

    for (const item of filterdItems) {
      if (!dependencyGraph.hasNode(item.id)) {
        dependencyGraph.addNode(item.id);
      }
      item.linkedTemplateIdsByThis.forEach(id => {
        if (filterdItems.some(item => item.id === id)) {
          if (!dependencyGraph.hasNode(id)) {
            dependencyGraph.addNode(id);
          }
          dependencyGraph.addDependency(id, item.id);
        }
      });
    }

    const orderedItemIds = dependencyGraph.overallOrder();

    const itemsById = keyBy(itemsInitial, i => i.id);

    return orderedItemIds.map(id => throwIfUndefined(itemsById[id]));
  }, [itemsInitial, viewer.organisation.id]);

  return (
    <BulkActionButton
      text={t("Delete Templates", {
        ns: "Templates",
      })}
      action={async item => {
        await deleteTemplate({
          variables: { input: { quoteTemplateId: item.id } },
        });
      }}
      items={items}
      startIcon={<DeleteIcon />}
      loading={loading}
      sequentially
      handleComplete={itemCount => {
        if (handleComplete) handleComplete();
        enqueueSnackbar(
          t(
            "{count, plural, =0 {No templates} one {One template} other {# templates}} deleted",
            {
              ns: "Templates",
              count: itemCount,
            }
          )
        );
      }}
      onFinalized={async () => {
        await client.refetchQueries({
          include: [namedOperations.Query.TemplateQuotes],
        });
      }}
    />
  );
}
